import { useState, useRef, useEffect, useCallback, LegacyRef, ReactEventHandler } from "react";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { MdOutlineFullscreen } from "react-icons/md";
import { MdOutlineFullscreenExit } from "react-icons/md";
import { MdOutlineDragIndicator } from "react-icons/md";
import { theme } from "../../theme";
import IconButton from "@mui/material/IconButton";

const DEFAULT_R = 24;
const DEFAULT_B = 24;
const MIN_W = 260;
const MIN_H = 320;
const DEFAULT_W = 490;
const DEFAULT_H = 650;
const FAB_BOTTOM = 48;
const FAB_RIGHT = 48;
export interface IProps {
  iframeSrc: string;
  title: string;
  iframeRef: LegacyRef<HTMLIFrameElement> | undefined;
  onIframeLoad?: ReactEventHandler<HTMLIFrameElement>;
  defaultOpen?: boolean;
}

export default function ResizableWindow({
  iframeSrc = "",
  iframeRef,
  title = "Chat",
  onIframeLoad = () => { },
  defaultOpen = false,
}: IProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [fullscreen, setFullscreen] = useState(false);

  const [pos, setPos] = useState({
    left: null,
    top: null,
    right: DEFAULT_R,
    bottom: DEFAULT_B,
  });
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [sizeHint, setSizeHint] = useState(`${DEFAULT_W} × ${DEFAULT_H}`);

  const winRef = useRef<any | null>(null);
  const savedRef = useRef<any | null>(null);
  const dragRef = useRef<any | null>(null);
  const rszRef = useRef<any | null>(null);

  // --- Drag (move) via left-side handle ---
  const onDragStart = useCallback(
    (e: any) => {
      if (fullscreen) return;
      const rect = winRef?.current.getBoundingClientRect();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origLeft: rect.left,
        origTop: rect.top,
      };
      setPos((p: any) => ({
        ...p,
        right: "auto",
        bottom: "auto",
        left: rect.left,
        top: rect.top,
      }));
      e.preventDefault();
    },
    [fullscreen],
  );

  // --- Resize ---
  const onResizeStart = useCallback(
    (dir: any) => (e: any) => {
      if (fullscreen) return;
      const rect = winRef.current.getBoundingClientRect();
      rszRef.current = {
        dir,
        startX: e.clientX,
        startY: e.clientY,
        origL: rect.left,
        origT: rect.top,
        origW: rect.width,
        origH: rect.height,
      };
      setPos((p: any) => ({
        ...p,
        right: "auto",
        bottom: "auto",
        left: rect.left,
        top: rect.top,
      }));
      e.preventDefault();
    },
    [fullscreen],
  );

  useEffect(() => {
    const onMove = (e: any) => {
      if (dragRef.current) {
        const { startX, startY, origLeft, origTop } = dragRef.current;
        setPos((p: any) => ({
          ...p,
          left: origLeft + e.clientX - startX,
          top: origTop + e.clientY - startY,
        }));
      }
      if (rszRef.current) {
        const { dir, startX, startY, origL, origT, origW, origH } =
          rszRef.current;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        let l = origL,
          t = origT,
          w = origW,
          h = origH;

        if (dir.includes("e")) w = Math.max(MIN_W, origW + dx);
        if (dir.includes("s")) h = Math.max(MIN_H, origH + dy);
        if (dir.includes("w")) {
          const nw = Math.max(MIN_W, origW - dx);
          l = origL + (origW - nw);
          w = nw;
        }
        if (dir.includes("n")) {
          const nh = Math.max(MIN_H, origH - dy);
          t = origT + (origH - nh);
          h = nh;
        }

        setPos((p) => ({ ...p, left: l, top: t }));
        setSize({ w, h });
        setSizeHint(`${Math.round(w)} × ${Math.round(h)}`);
      }
    };
    const onUp = () => {
      dragRef.current = null;
      rszRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // --- Fullscreen ---
  const toggleFullscreen = () => {
    if (!fullscreen) {
      savedRef.current = { pos, size };
      setFullscreen(true);
      setSizeHint("fullscreen");
    } else {
      // const { pos: p, size: s } = savedRef.current;
      // setPos(p);
      setPos({ left: null, top: null, right: DEFAULT_R, bottom: DEFAULT_B })
      const { size: s } = savedRef.current;
      setSize(s);
      setFullscreen(false);
      setSizeHint(`${Math.round(s.w)} × ${Math.round(s.h)}`);
    }
  };

  const windowStyle: any = fullscreen
    ? {
      position: "fixed",
      inset: 0,
      width: "100%",
      top: 60,
      height: "calc(100vh - 60px)",
      borderRadius: 0,
      border: "none",
    }
    : {
      position: "fixed",
      width: size.w,
      height: size.h,
      ...(pos.right !== ("auto" as any)
        ? { right: pos.right, bottom: pos.bottom }
        : { left: pos.left, top: pos.top }),
    };

  const RESIZE_DIRS = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];
  const cursorMap: any = {
    // n: "n-resize",
    // s: "s-resize",
    // e: "e-resize",
    w: "w-resize",
    // ne: "ne-resize",
    // nw: "nw-resize",
    // se: "se-resize",
    // sw: "sw-resize",
  };
  const handleStyle = (dir: any) => {
    const base: any = {
      position: "absolute",
      background: "transparent",
      zIndex: 10,
    };
    const edge: any = {
      // n: { top: 0, left: 8, right: 8, height: 6 },
      // s: { bottom: 0, left: 8, right: 8, height: 6 },
      // e: { top: 8, bottom: 8, right: 0, width: 6 },
      w: { top: 8, bottom: 8, left: 0, width: 6 },
      // ne: { top: 0, right: 0, width: 12, height: 12 },
      // nw: { top: 0, left: 0, width: 12, height: 12 },
      // se: { bottom: 0, right: 0, width: 12, height: 12 },
      // sw: { bottom: 0, left: 0, width: 12, height: 12 },
    };
    return { ...base, ...edge[dir], cursor: cursorMap[dir] };
  };

  return (
    <>
      {!open && (
        <Tooltip title="fd Pulse" placement="left">
          <Box
            sx={{
              position: "fixed",
              bottom: FAB_BOTTOM,
              right: FAB_RIGHT,
              zIndex: 1300,
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Pulse ring wrapper — centered only around the FAB */}
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                "&::before, &::after": {
                  content: '""',
                  position: "absolute",
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  opacity: 0,
                  animation: "fd-pulse 2.4s ease-out infinite",
                },
                "&::after": {
                  animationDelay: "1.2s",
                },
                "@keyframes fd-pulse": {
                  "0%": { transform: "scale(1)", opacity: 0.55 },
                  "100%": { transform: "scale(2.2)", opacity: 0 },
                },
              }}
            >
              <Fab
                color="primary"
                onClick={() => setOpen(true)}
                sx={{
                  width: 56,
                  height: 56,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.22)",
                  flexDirection: "column",
                  gap: "2px",
                  "&:hover": {
                    bgcolor: "primary.main",
                  },
                }}
              >
                {/* Pulse waveform SVG icon */}
                <svg
                  width="22"
                  height="14"
                  viewBox="0 0 22 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 7h2.5l2-5 2.5 10L10.5 4l2 6 1.5-3H22"
                    stroke="#fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Box
                  component="span"
                  sx={{
                    fontSize: "7.5px",
                    fontWeight: 700,
                    color: "#fff",
                    letterSpacing: "0.06em",
                    lineHeight: 1,
                    textTransform: "uppercase",
                    opacity: 0.92,
                  }}
                >
                  Pulse
                </Box>
              </Fab>
            </Box>
            <Box
              component="span"
              sx={{
                display: "block",
                textAlign: "center",
                fontSize: "9px",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                mt: "4px",
                whiteSpace: "nowrap",
                lineHeight: 1,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700, color: "primary.main" }}>
                Pulse:&nbsp;
              </Box>
              <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", textTransform: "none" }}>
                Ask me
              </Box>
            </Box>
          </Box>
        </Tooltip>
      )}

      <Box
        ref={winRef}
        sx={{
          background: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "12px",
          display: open ? "flex" : "none",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 9999,
          userSelect: "none",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          ...windowStyle,
        }}
      >
        {/* Resize handles */}
        {!fullscreen &&
          RESIZE_DIRS.map((dir) => (
            <div
              key={dir}
              style={handleStyle(dir)}
              onMouseDown={onResizeStart(dir)}
            />
          ))}

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            flexShrink: 0,
            borderBottom: "1px solid #eee",
            background: theme.palette.primary.main,
          }}
        >
          {/* Left drag strip */}
          {!fullscreen && (
            <div
              onMouseDown={onDragStart}
              title="Drag to move"
              style={{
                width: 18,
                cursor: "grab",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                borderRight: "1px solid #eee",
                color: "#fff",
                fontSize: 11,
                background: theme.palette.primary.main,
              }}
            >
              <MdOutlineDragIndicator />
            </div>
          )}

          {/* Title row */}
          <Box
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              padding: "10px 14px",
              gap: 8,
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: "bold",
                color: "#fff",
              }}
            >fd Pulse
            </span>
            <Tooltip title={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
              <IconButton
                size="medium"
                onClick={toggleFullscreen}
                title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                sx={hdrBtnStyle}
              >
                {fullscreen ? (
                  <MdOutlineFullscreenExit />
                ) : (
                  <MdOutlineFullscreen />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={"Close"}>
              <IconButton
                size="medium"
                onClick={() => {
                  setOpen(false);
                  setPos({ left: null, top: null, right: DEFAULT_R, bottom: DEFAULT_B });
                  if (fullscreen) setFullscreen(false);
                }}
                title="Close"
                sx={hdrBtnStyle}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </div>

        {/* Body — put your content here */}
        <div style={{ flex: 1, overflow: "auto" }}>
          {iframeSrc && (
            <iframe
              onLoad={onIframeLoad}
              src={iframeSrc}
              ref={iframeRef}
              title={title}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }}
              allow="microphone; camera"
            />
          )}
        </div>
      </Box>
    </>
  );
}

const hdrBtnStyle = {
  width: 28,
  height: 28,
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "25px",
  color: "#fff",
  minWidth: "auto",
};
